import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';
import { ToastProvider } from '../ui/Toast';
import { ModalProvider } from '../ui/Modal';

export interface DashboardShellProps {
  currentView?: string | undefined;
  onNavigate?: ((view: string) => void) | undefined;
  activeAppId?: string | undefined;
  title?: string | undefined;
  children?: React.ReactNode | undefined;
}

export function DashboardShell({
  currentView,
  onNavigate,
  activeAppId = '',
  title = 'DPDP Consent Dashboard',
  children,
}: DashboardShellProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <ModalProvider>
      <ToastProvider>
        <div className="flex h-screen w-full overflow-hidden bg-surface text-ink font-sans">
          {/* Desktop Sidebar */}
          <div className="hidden lg:flex shrink-0">
            <Sidebar
              currentView={currentView}
              onNavigate={onNavigate}
              activeAppId={activeAppId}
            />
          </div>

          {/* Mobile Navigation Drawer Backdrop */}
          {mobileMenuOpen && (
            <div
              className="fixed inset-0 z-40 bg-slate-950/60 nav-blur lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
          )}

          {/* Mobile Navigation Drawer */}
          <div
            className={`fixed top-0 bottom-0 left-0 z-50 w-72 bg-card border-r border-line shadow-cardhover transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between p-4 border-b border-line">
              <span className="font-extrabold text-sm text-ink">Navigation</span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-lg p-1 text-sub hover:text-ink hover:bg-slate-500/10"
                aria-label="Close navigation drawer"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <Sidebar
                currentView={currentView}
                onNavigate={(view) => {
                  setMobileMenuOpen(false);
                  onNavigate?.(view);
                }}
                activeAppId={activeAppId}
                className="w-full h-full border-r-0"
              />
            </div>
          </div>

          {/* Main Viewport */}
          <div className="flex flex-1 flex-col overflow-hidden min-w-0">
            <Header
              title={title}
              onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
            />

            {/* Scrollable Viewport */}
            <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
              {children || <Outlet />}
            </main>

            <Footer />
          </div>
        </div>
      </ToastProvider>
    </ModalProvider>
  );
}
